import {
    Autocomplete,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Stack,
    Switch,
    TextField
} from '@mui/material'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { Button } from 'src/components/forms'
import { HttpWFInicioFirma } from './api'
import { Opcion, WFInicioFirma, WFInicioFirmaCatalogos, WFInicioFirmaGuardar } from './types'

type Props = {
    open: boolean
    sinCodSistema: number
    catalogos: WFInicioFirmaCatalogos
    relacion: WFInicioFirma | null
    ordenSugerido: number
    onClose: () => void
    onSaved: () => void
}

const buscarOpcion = (opciones: Opcion[], id?: number | null) =>
    opciones.find(opcion => opcion.id === id) ?? null

const opcionSinGrupo: Opcion = { id: -1, descripcion: 'Sin grupo / Todos' }

const DialogWFInicioFirma = ({
    open,
    sinCodSistema,
    catalogos,
    relacion,
    ordenSugerido,
    onClose,
    onSaved
}: Props) => {
    const [etapa, setEtapa] = useState<Opcion | null>(null)
    const [grupo, setGrupo] = useState<Opcion | null>(null)
    const [resultado, setResultado] = useState<Opcion | null>(null)
    const [requiereResultado, setRequiereResultado] = useState(true)
    const [activo, setActivo] = useState(true)
    const [orden, setOrden] = useState<number>(ordenSugerido)

    useEffect(() => {
        setEtapa(buscarOpcion(catalogos.etapas, relacion?.intIdEtapa))
        setGrupo(buscarOpcion(catalogos.grupos, relacion?.intIdGrupo))
        setResultado(buscarOpcion(catalogos.resultados, relacion?.tinCodFirmaResultadoTipo))
        setRequiereResultado(relacion?.bitRequiereResultado ?? true)
        setActivo(relacion?.bitActivo ?? true)
        setOrden(relacion?.tinOrden ?? ordenSugerido)
    }, [catalogos, relacion, ordenSugerido, open])

    const guardar = () => {
        if (!etapa || !resultado || orden < 0 || orden > 255) {
            toast.error('Completá los campos obligatorios')
            
return Promise.reject()
        }

        const data: WFInicioFirmaGuardar = {
            intIdEtapa: etapa.id,
            intIdGrupo: grupo?.id ?? null,
            bitRequiereResultado: requiereResultado,
            tinCodFirmaResultadoTipo: resultado.id,
            bitActivo: activo,
            tinOrden: orden
        }

        const request: Promise<unknown> = relacion
            ? HttpWFInicioFirma.update(sinCodSistema, relacion.intIdWFInicioFirma, data)
            : HttpWFInicioFirma.create(sinCodSistema, data)

        return toast.promise(request, {
            loading: relacion ? 'Actualizando relación...' : 'Agregando relación...',
            success: relacion ? 'Relación actualizada' : 'Relación agregada',
            error: 'No se pudo guardar la relación'
        }).then(() => {
            onSaved()
            onClose()
        })
    }

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth='sm'>
            <DialogTitle>{relacion ? 'Editar inicio de firma' : 'Agregar inicio de firma'}</DialogTitle>
            <DialogContent>
                <Stack spacing={3} sx={{ pt: 1 }}>
                    <Autocomplete
                        options={catalogos.etapas}
                        value={etapa}
                        onChange={(_, value) => setEtapa(value)}
                        getOptionLabel={option => `${option.descripcion} (${option.id})`}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        renderInput={params => <TextField {...params} label='Etapa *' />}
                    />
                    <Autocomplete
                        options={[opcionSinGrupo, ...catalogos.grupos]}
                        value={grupo ?? opcionSinGrupo}
                        onChange={(_, value) => setGrupo(value?.id === opcionSinGrupo.id ? null : value)}
                        getOptionLabel={option => option.descripcion}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        renderInput={params => <TextField {...params} label='Grupo (opcional)' />}
                    />
                    <Autocomplete
                        options={catalogos.resultados}
                        value={resultado}
                        onChange={(_, value) => setResultado(value)}
                        getOptionLabel={option => option.descripcion}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        renderInput={params => <TextField {...params} label='Tipo de resultado *' />}
                    />
                    <TextField
                        label='Orden *'
                        type='number'
                        value={orden}
                        onChange={event => setOrden(Number(event.target.value))}
                        inputProps={{ min: 0, max: 255 }}
                    />
                    <Stack direction='row' spacing={3}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={requiereResultado}
                                    onChange={event => setRequiereResultado(event.target.checked)}
                                />
                            }
                            label='Requiere resultado'
                        />
                        {relacion && (
                            <FormControlLabel
                                control={<Switch checked={activo} onChange={event => setActivo(event.target.checked)} />}
                                label='Activo'
                            />
                        )}
                    </Stack>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button variant='text' color='inherit' onClick={onClose}>Cancelar</Button>
                <Button variant='contained' promise={guardar}>Guardar</Button>
            </DialogActions>
        </Dialog>
    )
}

export default DialogWFInicioFirma
